import dayjs from 'dayjs';

const RAW_FALSY = new Set<any>([null, undefined, '', 0, false]);

const isFalsyLike = (v: any): boolean => {
  if (RAW_FALSY.has(v)) return true;
  if (Array.isArray(v) && v.length === 0) return true;
  // String "0" should also be considered falsy-like in form contexts
  if (v === '0') return true;
  return false;
};

const isPlainObject = (x: any): x is Record<string, any> =>
  x !== null &&
  typeof x === 'object' &&
  !Array.isArray(x) &&
  !dayjs.isDayjs(x) &&
  !(x instanceof Date);

export const deepCompare = (a: any, b: any): boolean => {
  // Treat undefined, null, "", 0, "0", false as all equivalent
  // This prevents false positives in form data where empty/zero values
  // might be represented differently (e.g., "" vs "0" vs undefined vs 0)
  if (isFalsyLike(a) && isFalsyLike(b)) return true;

  if (dayjs.isDayjs(a) && dayjs.isDayjs(b)) return a.valueOf() === b.valueOf();
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepCompare(a[i], b[i])) return false;
    }
    return true;
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (!deepCompare(a[key], b[key])) return false;
    }
    return true;
  }

  return a === b;
};

export type DeepCompareDiff = { path: string; a: any; b: any };

/**
 * Collect the paths where `deepCompare(a, b)` would return false.
 * Uses the exact same equivalence rules as `deepCompare` (falsy-like,
 * dayjs, Date, arrays, plain objects), so a returned empty array is
 * equivalent to `deepCompare(a, b) === true`.
 */
export const findDeepCompareDiffs = (a: any, b: any, basePath = ''): DeepCompareDiff[] => {
  if (isFalsyLike(a) && isFalsyLike(b)) return [];

  if (dayjs.isDayjs(a) && dayjs.isDayjs(b)) {
    return a.valueOf() === b.valueOf() ? [] : [{ path: basePath || '<root>', a, b }];
  }
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime() ? [] : [{ path: basePath || '<root>', a, b }];
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return [{ path: `${basePath || '<root>'}.length`, a: a.length, b: b.length }];
    }
    const out: DeepCompareDiff[] = [];
    for (let i = 0; i < a.length; i++) {
      out.push(...findDeepCompareDiffs(a[i], b[i], `${basePath}[${i}]`));
    }
    return out;
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    const out: DeepCompareDiff[] = [];
    for (const key of keys) {
      const childPath = basePath ? `${basePath}.${key}` : key;
      out.push(...findDeepCompareDiffs(a[key], b[key], childPath));
    }
    return out;
  }

  return a === b ? [] : [{ path: basePath || '<root>', a, b }];
};
